import React, { type ReactElement } from 'react';
import {
  Box,
  Button,
  Illustration,
  type IllustrationKind,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../hooks';

import './SodReloader.css';

interface SodReloaderProps {
  triggerRefresh: () => void;
}

/**
 * A fallback UI component displayed when the laod fails.
 *
 * This component shows an error illustration, a descriptive message,
 * and a "Refresh" button to allow users to retry loading the data.
 *
 * @component
 * @example
 * <SodReloader triggerRefresh={() => refetchData()} />
 *
 * @param {Object} props - The component props.
 * @param {() => void} props.triggerRefresh - Callback function to trigger a refresh or retry action.
 * @returns {ReactElement} A UI element representing an error state with a retry option.
 */
function SodReloader({ triggerRefresh }: SodReloaderProps): ReactElement {
  const l = useInvertedLocalize();

  return (
    <Box tag='div' className='Sod-Relaoder-illustrationWrapper'>
      <Illustration kind={'Error' as IllustrationKind} />{' '}
      {/* eslint-disable-line @typescript-eslint/no-unsafe-assignment */}
      <Typography kind='h1' desktop='h1-d' mobile='h1-d'>
        {l('Something went wrong while loading the page.')}
      </Typography>
      <Typography
        kind='body1'
        desktop='body1-d'
        mobile='body1-d'
        className='Sod-Relaoder-illustrationMessage'>
        {l(
          'Try refreshing, or check back later. If the issue persists, contact your administrator.'
        )}
      </Typography>
      <Button type='button' kind='filled' onClick={triggerRefresh}>
        {l('Refresh')}
      </Button>
    </Box>
  );
}

export default SodReloader;
